import PersonAddOutlined from '@mui/icons-material/PersonAddOutlined';
import type { Client } from '@src/clients/domain/Client';
import type { ClientUser } from '@src/clientUser/manage/ClientUser';
import {
  ClientUserNameField,
  ClientUserPhoneField,
} from '@src/clientUser/manage/ClientUserFormInputs';
import { useEditClientUser } from '@src/clientUser/manage/update/useEditClientUser';
import { BaseModal } from '@src/common/components/BaseModal';
import { LoadingOverlay } from '@src/common/components/LoadingOverlay';
import { CloseConfirmModalFooter } from '@src/common/components/modal/CloseConfirmModalFooter';
import { Stack } from '@src/common/components/Stack';
import { TextInput } from '@src/common/components/TextInput';
import { useToast } from '@src/common/hooks/useToast';
import { theme } from '@src/common/styles/Theme';
import { useTranslation } from 'react-i18next';

export const EditClientUserModal = ({
  client,
  clientUser,
  onClose,
  onEdited,
}: {
  client: Client;
  clientUser?: ClientUser;
  onClose: () => void;
  onEdited: () => void;
}) => {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToast();

  const { errors, register, submit, reset, isLoading } = useEditClientUser({
    client,
    clientUser,
    onSuccess: () => {
      showSuccessToast(
        t('clientUsers.editModal.onSuccess', { clientName: client.name }),
      );
      onEdited();
      reset();
    },
    onError: () =>
      showErrorToast(
        t('clientUsers.editModal.onError', { clientName: client.name }),
      ),
  });

  return (
    <BaseModal
      open={!!clientUser}
      onClose={onClose}
      title={t('clientUsers.editModal.title', { clientName: client.name })}
      titleIcon={<PersonAddOutlined />}
    >
      {isLoading && <LoadingOverlay />}
      <Stack gap={theme.spacing['3']}>
        <TextInput
          label={t('name')}
          placeholder={t('clientUsers.fields.name.placeholder')}
          error={
            errors[ClientUserNameField.fieldName] &&
            t(`formErrors.${errors[ClientUserNameField.fieldName]!.message}`)
          }
          {...register(ClientUserNameField.fieldName)}
        />

        <TextInput
          label={t('phone')}
          placeholder={t('clientUsers.fields.phone.placeholder')}
          error={
            errors[ClientUserPhoneField.fieldName] &&
            t(`formErrors.${errors[ClientUserPhoneField.fieldName]!.message}`)
          }
          {...register(ClientUserPhoneField.fieldName)}
        />

        <CloseConfirmModalFooter
          onClose={() => {
            onClose();
            reset();
          }}
          confirm={submit}
          confirmLabel={t('save')}
        />
      </Stack>
    </BaseModal>
  );
};
